import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  useAssinaturasAgente,
  useAudienciasAgente,
  useContatosAgente,
  useSalvarAssinaturaAgente,
  type CanalAgente,
} from "@/hooks/use-agent";
import { Loader2, Mail, MessageCircle } from "lucide-react";

// Aba Envio automático de /agente — as assinaturas do boletim (Ciclo 2; canal
// WhatsApp real desde o Ciclo 3a).
//
// Uma AUDIÊNCIA (pessoa ou grupo) pode ter mais de uma assinatura simultânea —
// email E WhatsApp ao mesmo tempo, cada uma com seu próprio horário e
// interruptor (migração 036 — ver server/storage/agent.ts). Por isso a
// renderização é por (audiência × canal), não uma linha só por pessoa.
//
// ⚠️ UM interruptor só (08/2026). Ligar o canal aqui liga as DUAS entregas: o
// diário de terça a sábado e o consolidado semanal no sábado. O semanal não tem
// assinatura própria (o PUT /api/agent/assinaturas recusa `boletim.semanal`) —
// ele sai no cron para as mesmas audiências e canais do diário. Um segundo
// toggle daria duas verdades sobre "quem recebe o boletim".

/**
 * Só horas cheias: a agenda do servidor (o cron da Vercel) visita o handler no
 * minuto 0 de cada hora. Oferecer 06:30 aqui daria uma assinatura que nunca
 * dispara — e falharia em silêncio, que é o pior desfecho possível.
 */
const HORARIOS = ["04:00", "05:00", "06:00", "07:00", "08:00", "09:00", "12:00", "18:00"];

const MOTIVO_CLOUD_INDISPONIVEL = "Aguardando verificação da empresa no Meta.";

interface CanalDisponivel {
  canal: CanalAgente;
  rotulo: string;
  Icone: typeof Mail;
  disponivel: boolean;
  motivo?: string;
}

interface LinhaAudiencia {
  audienciaId: string;
  nome: string;
  tipo: "contato" | "grupo";
  destino: string;
  canais: CanalDisponivel[];
}

const CANAL_WHATSAPP_CLOUD: CanalDisponivel = {
  canal: "whatsapp_cloud",
  rotulo: "WhatsApp (oficial)",
  Icone: MessageCircle,
  disponivel: false,
  motivo: MOTIVO_CLOUD_INDISPONIVEL,
};

export function EnvioAutomatico() {
  const { toast } = useToast();
  const contatos = useContatosAgente();
  const audiencias = useAudienciasAgente();
  const assinaturas = useAssinaturasAgente();
  const salvar = useSalvarAssinaturaAgente();

  // Qual (audiência, canal) está sendo salvo — o spinner é por sub-linha, não da aba inteira.
  const [salvando, setSalvando] = useState<string | null>(null);
  const chaveSalvando = (audienciaId: string, canal: CanalAgente) => `${audienciaId}:${canal}`;

  const carregando = contatos.isLoading || audiencias.isLoading || assinaturas.isLoading;
  const erro = contatos.isError || audiencias.isError || assinaturas.isError;

  const linhas: LinhaAudiencia[] = [];

  (contatos.data ?? []).filter(c => c.agentAudienciaId).forEach(c => {
    const canais: CanalDisponivel[] = [];
    if (c.email) canais.push({ canal: "email", rotulo: "Email", Icone: Mail, disponivel: true });
    if (c.telefone) {
      canais.push({ canal: "whatsapp_gzappy", rotulo: "WhatsApp (GZappy)", Icone: MessageCircle, disponivel: true });
    }
    canais.push(CANAL_WHATSAPP_CLOUD);
    linhas.push({
      audienciaId: c.agentAudienciaId!,
      nome: c.nome,
      tipo: "contato",
      destino: c.email ?? c.telefone ?? "sem destino",
      canais,
    });
  });

  (audiencias.data ?? []).filter(a => a.tipo === "grupo").forEach(a => {
    linhas.push({
      audienciaId: a.id,
      nome: a.nome,
      tipo: "grupo",
      destino: a.destino,
      canais: [
        { canal: "whatsapp_gzappy", rotulo: "WhatsApp (GZappy) · grupo", Icone: MessageCircle, disponivel: true },
        CANAL_WHATSAPP_CLOUD,
      ],
    });
  });

  const assinaturaDe = (audienciaId: string, canal: CanalAgente) =>
    (assinaturas.data ?? []).find(a =>
      a.agentAudienciaId === audienciaId && a.canal === canal && a.capacidade === "boletim.diario");

  const aplicar = (audienciaId: string, canal: CanalAgente, patch: { horario?: string; ativo?: boolean }) => {
    const atual = assinaturaDe(audienciaId, canal);
    setSalvando(chaveSalvando(audienciaId, canal));
    salvar.mutate(
      {
        agentAudienciaId: audienciaId,
        capacidade: "boletim.diario",
        canal,
        horario: patch.horario ?? atual?.horario ?? "06:00",
        ativo: patch.ativo ?? atual?.ativo ?? false,
      },
      {
        onSuccess: () => {
          setSalvando(null);
          toast({ title: "Assinatura atualizada" });
        },
        onError: e => {
          setSalvando(null);
          toast({
            title: "Não consegui salvar",
            description: e instanceof Error ? e.message : String(e),
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-semibold">Envio automático</h2>
        <p className="text-sm text-muted-foreground">
          Cada canal tem seu próprio horário e interruptor — email e WhatsApp da mesma pessoa podem ficar ligados ao
          mesmo tempo.
        </p>
      </div>

      {carregando ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : erro ? (
        <p className="text-sm text-erro py-6 text-center" data-testid="erro-assinaturas">
          {((contatos.error ?? audiencias.error ?? assinaturas.error) as any)?.message ?? "Não foi possível carregar as assinaturas."}
        </p>
      ) : linhas.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          Cadastre um contato ou grupo na aba <strong>Contatos</strong> para poder ligar o boletim.
        </p>
      ) : (
        <ul className="space-y-3">
          {linhas.map(linha => (
            <li key={linha.audienciaId} className="rounded-md border p-3 space-y-3" data-testid={`audiencia-${linha.audienciaId}`}>
              <div>
                <p className="font-medium">{linha.nome}</p>
                <p className="text-xs text-muted-foreground break-all">
                  {linha.destino} · terça a sábado · movimento do dia anterior
                </p>
              </div>

              <ul className="space-y-2">
                {linha.canais.map(c => {
                  const atual = assinaturaDe(linha.audienciaId, c.canal);
                  const chave = chaveSalvando(linha.audienciaId, c.canal);
                  const horario = atual?.horario ?? "06:00";
                  const ativo = atual?.ativo ?? false;

                  if (!c.disponivel) {
                    return (
                      <li key={c.canal} className="flex items-center justify-between gap-2 opacity-60" data-testid={`canal-indisponivel-${linha.audienciaId}-${c.canal}`}>
                        <span className="text-sm flex items-center gap-1.5">
                          <c.Icone className="h-3.5 w-3.5 shrink-0" />
                          {c.rotulo}
                        </span>
                        <span className="text-xs text-muted-foreground">{c.motivo}</span>
                      </li>
                    );
                  }

                  return (
                    <li key={c.canal} className="flex items-center justify-between gap-2 flex-wrap" data-testid={`canal-${linha.audienciaId}-${c.canal}`}>
                      <span className="text-sm flex items-center gap-1.5 shrink-0">
                        <c.Icone className="h-3.5 w-3.5 shrink-0" />
                        {c.rotulo}
                      </span>
                      <div className="flex items-center gap-2">
                        <Select
                          value={horario}
                          disabled={salvando === chave}
                          onValueChange={v => aplicar(linha.audienciaId, c.canal, { horario: v })}
                        >
                          <SelectTrigger className="w-24 h-8 text-xs" data-testid={`select-horario-${linha.audienciaId}-${c.canal}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {HORARIOS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {salvando === chave && <Loader2 className="h-4 w-4 animate-spin" />}
                        <Switch
                          checked={ativo}
                          disabled={salvando === chave}
                          onCheckedChange={v => aplicar(linha.audienciaId, c.canal, { ativo: v })}
                          data-testid={`switch-${linha.audienciaId}-${c.canal}`}
                          aria-label={`Recebe os boletins (diário e semanal) por ${c.rotulo}`}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}

      {/* Sem interruptor próprio de propósito — ver o comentário no topo do
          arquivo. O card existe para dizer QUANDO o segundo envio do sábado
          acontece; quem decide se ele acontece são os canais ligados acima. */}
      <div className="rounded-md border p-3">
        <p className="font-medium">Boletim semanal</p>
        {/* Sem hora cravada: o horário é do canal, escolhido no seletor logo
            acima (e pode ser diferente por audiência × canal). O semanal sai no
            MESMO horário do diário, porque é a mesma assinatura. */}
        <p className="text-sm text-muted-foreground">
          sábado, no mesmo horário do diário · consolidado de segunda a sexta · segue os canais ativos acima
        </p>
      </div>

      {/* Duas consequências declaradas, as duas do desenho da agenda
          (server/agent/agenda.ts): ela pergunta "já venceu hoje?" em vez de
          "vence neste minuto" (quem liga à tarde confirma na hora), e domingo e
          segunda não são dias de envio — não é tique perdido, é regra. */}
      <p className="text-xs text-muted-foreground">
        No sábado chegam <strong>duas</strong> mensagens: o boletim de sexta e, logo depois, o consolidado da semana.
        Ligando um canal depois do horário num dia de envio, o boletim de ontem chega em até uma hora. Domingo e
        segunda não sai boletim: ligando num desses dias, o primeiro chega na terça, no horário escolhido.
      </p>
    </div>
  );
}
